import React, { useEffect, useState, useMemo, memo, useCallback } from 'react';
import { FiFileText } from 'react-icons/fi';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, BorderStyle, convertInchesToTwip, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import AdminLayout from './AdminLayout';
import { useNavRefresh } from '../../context/NavContext';
import API from '../../utils/api';
import styles from './AdminQuestions.module.css';

const SECTIONS = [
  { key: 'cognitive', label: 'Cognitive & Analytical Skills (MCQ - 50)' },
  { key: 'personality', label: 'Personality & Emotional Intelligence (Likert - 50)' },
  { key: 'leadership', label: 'Leadership & Team Management (Scenario - 20)' },
  { key: 'casestudy', label: 'Technical Case Study (Open - 5)' },
];

const ROLES = [
  { value: 'all', label: 'Shared (All Roles)' },
  { value: 'RSM', label: 'RSM Only' },
  { value: 'FSM', label: 'FSM Only' },
  { value: 'TSE', label: 'TSE Only' },
];

const TYPES = [
  { value: 'mcq', label: 'Multiple Choice' },
  { value: 'likert', label: 'Likert Scale' },
  { value: 'scenario', label: 'Scenario' },
  { value: 'open', label: 'Open-Ended' },
];

const QuestionItemMemo = memo(function QuestionItem({ q, editingId, editData, setEditingId, setEditData, handleSaveEdit, handleDelete, handleEdit, ROLES, styles }) {
  const isEditing = editingId === q._id;

  if (isEditing) {
    return (
      <div key={q._id} className={styles.questionItem}>
        <div className={styles.editForm}>
          <textarea
            className={styles.textInput}
            value={editData.text}
            onChange={e => setEditData({ ...editData, text: e.target.value })}
            rows={3}
          />
          {/* Options for MCQ/Scenario */}
          {(editData.type === 'mcq' || editData.type === 'scenario') && (
            <div className={styles.optionsSection}>
              <label className={styles.optionsLabel}>Options:</label>
              {(editData.options || []).map((opt, idx) => (
                <div key={idx} className={styles.optionRow}>
                  <input
                    type="text"
                    placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                    value={typeof opt === 'string' ? opt : (opt.text || opt.label || '')}
                    onChange={e => {
                      const newOpts = [...(editData.options || [])];
                      newOpts[idx] = typeof opt === 'string' 
                        ? e.target.value 
                        : { ...opt, text: e.target.value };
                      setEditData({ ...editData, options: newOpts });
                    }}
                    className={styles.optionInput}
                  />
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => {
                      setEditData({
                        ...editData,
                        options: editData.options.filter((_, i) => i !== idx)
                      });
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.addOptionBtn}
                onClick={() => {
                  setEditData({
                    ...editData,
                    options: [...(editData.options || []), { label: '', text: '' }]
                  });
                }}
              >
                + Add Option
              </button>
            </div>
          )}
          <div className={styles.editRow}>
            <select
              value={editData.roleLevel || 'all'}
              onChange={e => setEditData({ ...editData, roleLevel: e.target.value })}
              className={styles.smallInput}
            >
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Correct Answer"
              value={editData.correctAnswer}
              onChange={e => setEditData({ ...editData, correctAnswer: e.target.value })}
              className={styles.smallInput}
            />
            <input
              type="number"
              placeholder="Max Score"
              value={editData.maxScore}
              onChange={e => setEditData({ ...editData, maxScore: parseInt(e.target.value) })}
              className={styles.smallInput}
            />
          </div>
          <div className={styles.editActions}>
            <button className={styles.saveBtn} onClick={() => handleSaveEdit(q._id)}>
              Save
            </button>
            <button className={styles.cancelBtn} onClick={() => setEditingId(null)}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div key={q._id} className={styles.questionItem}>
      <div className={styles.questionContent}>
        <span className={styles.qNum}>Q{q.questionNumber}</span>
        <div className={styles.qText}>{q.text}</div>
        <div className={styles.qMeta}>
          <span className={styles.badge}>{q.type}</span>
          <span className={styles.roleBadge}>
            {ROLES.find(r => r.value === q.roleLevel)?.label || 'All'}
          </span>
          {q.correctAnswer && <span className={styles.answer}>Answer: {q.correctAnswer}</span>}
          <span className={styles.score}>Max: {q.maxScore}</span>
        </div>
      </div>
      <div className={styles.questionActions}>
        <button className={styles.editBtn} onClick={() => handleEdit(q)}>
          Edit
        </button>
        <button className={styles.deleteBtn} onClick={() => handleDelete(q._id)}>
          Delete
        </button>
      </div>
    </div>
  );
});

export default function AdminQuestions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('cognitive');
  const [activeRole, setActiveRole] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    section: 'cognitive',
    roleLevel: 'all',
    text: '',
    type: 'mcq',
    options: [],
    correctAnswer: '',
    maxScore: 2
  });
  const { refreshKey } = useNavRefresh();

  const loadQuestions = useCallback(async () => {
    try {
      const res = await API.get('/questions/admin/all');
      setQuestions(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [refreshKey, loadQuestions]);

  const sectionQuestions = useMemo(() => 
    questions.filter(q => 
      q.section === activeSection && 
      (q.roleLevel === activeRole || q.roleLevel === 'all')
    ),
    [questions, activeSection, activeRole]
  );

  const handleEdit = useCallback(q => {
    setEditingId(q._id);
    setEditData({ ...q });
  }, []);

  const handleSaveEdit = useCallback(async id => {
    try {
      await API.put(`/questions/admin/${id}`, editData);
      setQuestions(p => p.map(q => q._id === id ? editData : q));
      setEditingId(null);
    } catch (e) {
      console.error(e);
    }
  }, [editData]);

  const handleDelete = useCallback(async id => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await API.delete(`/questions/admin/${id}`);
        setQuestions(p => p.filter(q => q._id !== id));
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const handleAddQuestion = useCallback(async e => {
    e.preventDefault();
    try {
      const res = await API.post('/questions/admin', {
        ...formData,
        questionNumber: sectionQuestions.length + 1
      });
      setQuestions(p => [...p, res.data]);
      setFormData({
        section: 'cognitive',
        roleLevel: 'all',
        text: '',
        type: 'mcq',
        options: [],
        correctAnswer: '',
        maxScore: 2
      });
      setShowForm(false);
    } catch (e) {
      console.error(e);
    }
  }, [formData, sectionQuestions.length]);

  const currentSection = SECTIONS.find(s => s.key === activeSection);

  const handleExportToWord = useCallback(async () => {
    try {
      // Get all questions from the API (all roles and sections)
      const res = await API.get('/questions/admin/all');
      const allQuestions = res.data;

      // Group questions by role and section
      const rolesSorted = ['all', 'RSM', 'FSM', 'TSE'];
      const sections = [];

      rolesSorted.forEach(roleName => {
        const roleLabel = roleName === 'all' ? 'Shared (All Roles)' : `${roleName} Only`;
        const roleQuestions = allQuestions.filter(q => q.roleLevel === roleName);
        
        if (roleQuestions.length === 0) return;

        // Add role heading
        sections.push(
          new Paragraph({
            text: `${roleLabel}`,
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
            border: {
              bottom: {
                color: '000000',
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
          })
        );

        // Group by section
        SECTIONS.forEach(section => {
          const sectionQuestions = roleQuestions.filter(q => q.section === section.key);
          if (sectionQuestions.length === 0) return;

          // Add section subheading
          sections.push(
            new Paragraph({
              text: section.label,
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 150 },
            })
          );

          // Add questions for this section
          sectionQuestions.forEach((q, idx) => {
            // Question number and text
            sections.push(
              new Paragraph({
                text: `Q${idx + 1}: ${q.text}`,
                spacing: { before: 100, after: 50 },
                style: 'Normal',
              })
            );

            // Options (if applicable)
            if ((q.type === 'mcq' || q.type === 'scenario') && q.options && q.options.length > 0) {
              q.options.forEach((opt, optIdx) => {
                const optText = typeof opt === 'string' ? opt : (opt.text || opt.label || '');
                const letter = String.fromCharCode(65 + optIdx);
                sections.push(
                  new Paragraph({
                    text: `${letter}. ${optText}`,
                    spacing: { before: 25, after: 25 },
                    indent: { left: 360 },
                  })
                );
              });
            }

            // Correct Answer
            if (q.correctAnswer) {
              sections.push(
                new Paragraph({
                  text: `Answer: ${q.correctAnswer}`,
                  spacing: { before: 50, after: 100 },
                  indent: { left: 360 },
                  style: 'Normal',
                })
              );
            }
          });
        });

        // Add page break between roles
        if (roleName !== rolesSorted[rolesSorted.length - 1]) {
          sections.push(
            new Paragraph({
              text: '',
              pageBreakBefore: true,
            })
          );
        }
      });

      // Create and save the document
      const doc = new Document({
        sections: [
          {
            children: [
              new Paragraph({
                text: 'Assessment Questions Export',
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 100 },
              }),
              new Paragraph({
                text: `Exported on: ${new Date().toLocaleDateString()}`,
                spacing: { after: 400 },
                style: 'Normal',
              }),
              ...sections,
            ],
          },
        ],
      });

      Packer.toBlob(doc).then(blob => {
        saveAs(blob, `Assessment_Questions_${new Date().getTime()}.docx`);
      });
    } catch (e) {
      console.error('Export failed:', e);
      alert('Failed to export questions to Word');
    }
  }, []);

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h2 className={styles.pageTitle}>Assessment Questions</h2>
            <p className={styles.pageSub}>Manage questions and answers for all assessment sections</p>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.actionBtn} onClick={handleExportToWord}>
              <FiFileText aria-hidden="true" /> Export to Word
            </button>
            <button className={styles.actionBtn} onClick={() => setShowForm(true)}>
              + Add Question
            </button>
          </div>
        </div>

        {/* Role Filter */}
        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>Filter by Role:</label>
          <select 
            value={activeRole} 
            onChange={e => setActiveRole(e.target.value)}
            className={styles.roleFilter}
          >
            {ROLES.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>

        {/* Section Tabs */}
        <div className={styles.tabs}>
          {SECTIONS.map(sec => (
            <button
              key={sec.key}
              className={`${styles.tab} ${activeSection === sec.key ? styles.tabActive : ''}`}
              onClick={() => setActiveSection(sec.key)}
            >
              {sec.label}
            </button>
          ))}
        </div>

        {/* Questions List */}
        {loading ? (
          <div className={styles.loading}>Loading questions...</div>
        ) : (
          <div className={styles.questionsList}>
            <div className={styles.sectionHeader}>
              <span>{currentSection.label} - Total: {sectionQuestions.length}</span>
            </div>

            {sectionQuestions.length === 0 ? (
              <div className={styles.empty}>No questions yet in this section</div>
            ) : (
              sectionQuestions.map(q => (
                <QuestionItemMemo 
                  key={q._id}
                  q={q}
                  editingId={editingId}
                  editData={editData}
                  setEditingId={setEditingId}
                  setEditData={setEditData}
                  handleSaveEdit={handleSaveEdit}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  ROLES={ROLES}
                  styles={styles}
                />
              ))
            )}
          </div>
        )}

        {/* Add Question Form */}
        {showForm && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <h3 className={styles.modalTitle}>Add New Question</h3>
              <form onSubmit={handleAddQuestion} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Section</label>
                  <select
                    value={formData.section}
                    onChange={e => setFormData({ ...formData, section: e.target.value })}
                    className={styles.input}
                  >
                    {SECTIONS.map(s => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Role Level</label>
                  <select
                    value={formData.roleLevel}
                    onChange={e => setFormData({ ...formData, roleLevel: e.target.value })}
                    className={styles.input}
                  >
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Question Text</label>
                  <textarea
                    value={formData.text}
                    onChange={e => setFormData({ ...formData, text: e.target.value })}
                    className={styles.textarea}
                    rows={4}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={e => {
                      const newType = e.target.value;
                      // Reset options if switching to/from MCQ/Scenario
                      const newOptions = (newType === 'mcq' || newType === 'scenario') ? formData.options : [];
                      setFormData({ ...formData, type: newType, options: newOptions });
                    }}
                    className={styles.input}
                  >
                    {TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Options for MCQ/Scenario */}
                {(formData.type === 'mcq' || formData.type === 'scenario') && (
                  <div className={styles.formGroup}>
                    <label>Options:</label>
                    {formData.options.map((opt, idx) => (
                      <div key={idx} className={styles.optionRow}>
                        <input
                          type="text"
                          placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                          value={typeof opt === 'string' ? opt : (opt.text || opt.label || '')}
                          onChange={e => {
                            const newOpts = [...formData.options];
                            newOpts[idx] = typeof opt === 'string' 
                              ? e.target.value 
                              : { ...opt, text: e.target.value };
                            setFormData({ ...formData, options: newOpts });
                          }}
                          className={styles.input}
                        />
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => {
                            setFormData({
                              ...formData,
                              options: formData.options.filter((_, i) => i !== idx)
                            });
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className={styles.addOptionBtn}
                      onClick={() => {
                        setFormData({
                          ...formData,
                          options: [...formData.options, { label: '', text: '' }]
                        });
                      }}
                    >
                      + Add Option
                    </button>
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label>Correct Answer</label>
                  <input
                    type="text"
                    value={formData.correctAnswer}
                    onChange={e => setFormData({ ...formData, correctAnswer: e.target.value })}
                    className={styles.input}
                    placeholder="e.g., A, B, C or numeric value"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Max Score</label>
                  <input
                    type="number"
                    value={formData.maxScore}
                    onChange={e => setFormData({ ...formData, maxScore: parseInt(e.target.value) })}
                    className={styles.input}
                    min="1"
                  />
                </div>

                <div className={styles.formActions}>
                  <button type="submit" className={styles.submitBtn}>
                    Add Question
                  </button>
                  <button type="button" className={styles.cancelBtn} onClick={() => setShowForm(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
